import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { fadeInUp, staggerContainer, scaleIn } from '../hooks/useScrollAnimation'

const projects = [
  {
    id: 1,
    title: 'pr-gen',
    subtitle: 'PR Description Generator using Claude API',
    description:
      'A CLI tool that automatically generates pull request descriptions using the Claude API (Anthropic). Analyzes git diffs and commit history to produce well-structured PR summaries instantly.',
    longDesc:
      'Built with Node.js and the Anthropic SDK. Reads staged changes, runs Claude to generate a complete PR description, and copies it to your clipboard. Saves minutes on every PR.',
    tech: ['JavaScript', 'Node.js', 'Claude API', 'Anthropic SDK', 'Git'],
    github: 'https://github.com/its-seraj/pr-gen',
    demo: null,
    featured: true,
    category: ['AI/ML', 'Backend'],
    stars: '—',
    status: 'open-source',
  },
  {
    id: 2,
    title: 'zeroTrade',
    subtitle: 'Zero-Brokerage Android Trading App',
    description:
      'Android trading app integrating Kotak Neo APIs with TOTP-based authentication for secure, zero-brokerage real-time stock order execution. Built for speed and reliability.',
    longDesc:
      'Written in Java, the app uses Kotak Neo REST APIs to place and track orders in real-time. TOTP authentication ensures secure access. Designed for traders who want zero brokerage with full control.',
    tech: ['Java', 'Android', 'Kotak Neo API', 'TOTP', 'REST APIs'],
    github: 'https://github.com/its-seraj/trade-bot',
    demo: null,
    featured: true,
    category: ['Backend'],
    stars: '—',
    status: 'production',
  },
  {
    id: 3,
    title: 'code-snippets',
    subtitle: 'Searchable Code Snippet Repository',
    description:
      'A full-stack web app managing 1000+ code snippets with JWT + OAuth authentication. Features full-text search, syntax highlighting, and tag-based organization.',
    longDesc:
      'Built to solve the pain of hunting through old projects for reusable code. Supports GitHub OAuth login, personal snippet collections, and fuzzy search across the entire library.',
    tech: ['JavaScript', 'Node.js', 'React', 'JWT', 'OAuth', 'MongoDB'],
    github: 'https://github.com/its-seraj/code-snippets',
    demo: null,
    featured: true,
    category: ['Frontend', 'Backend'],
    stars: '—',
    status: 'production',
  },
  {
    id: 4,
    title: 'port-killer',
    subtitle: 'CLI Tool to Kill Processes on Ports',
    description:
      'A Node.js CLI utility to list all open ports and kill the processes using them. Useful for developers who frequently deal with "port already in use" errors.',
    longDesc:
      'Cross-platform Node.js tool. Run port-killer to see all occupied ports, pick the one you want to free, and the process is terminated instantly. No more manual lsof/kill chains.',
    tech: ['JavaScript', 'Node.js', 'CLI', 'Bash'],
    github: 'https://github.com/its-seraj/port-killer',
    demo: null,
    featured: false,
    category: ['Backend'],
    stars: '—',
    status: 'open-source',
  },
  {
    id: 5,
    title: 'VectorBox',
    subtitle: 'SVG Vector Editor in the Browser',
    description:
      'A browser-based SVG vector editing tool. Draw, edit, and export SVG shapes without any desktop software. Built for developers who need quick vector graphics.',
    longDesc:
      'Supports shape creation, path editing, color fills, and SVG export. Entirely client-side — no backend required. Useful for generating icons, diagrams, or illustrations on the fly.',
    tech: ['JavaScript', 'HTML5', 'CSS3', 'SVG', 'Canvas API'],
    github: 'https://github.com/its-seraj/svg-icons',
    demo: null,
    featured: false,
    category: ['Frontend'],
    stars: '—',
    status: 'open-source',
  },
  {
    id: 6,
    title: 'diff-editor-ui',
    subtitle: 'Side-by-Side Code Diff Editor',
    description:
      'A web-based diff editor for comparing two code blocks side-by-side with syntax-highlighted differences. Clean, minimal UI with real-time diff computation.',
    longDesc:
      'Built with vanilla JavaScript and a diff algorithm. Paste two versions of code, see the differences highlighted instantly. Great for reviewing changes without a full IDE.',
    tech: ['JavaScript', 'HTML5', 'CSS3', 'Diff Algorithm'],
    github: 'https://github.com/its-seraj/diff-editor-ui',
    demo: null,
    featured: false,
    category: ['Frontend'],
    stars: '—',
    status: 'open-source',
  },
]

const categories = ['All', 'Frontend', 'Backend', 'DevOps', 'AI/ML']

function ProjectCard({ project, index }) {
  const [hovered, setHovered] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -60px 0px' })

  const statusColors = {
    production: '#00ff41',
    beta: '#ffa657',
    'open-source': '#00d4ff',
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08, ease: 'easeOut' }}
      className="relative glass-card border border-[#30363d] rounded-xl overflow-hidden group cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileHover={{ y: -4, borderColor: 'rgba(0,255,65,0.4)' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#30363d] bg-[#161b22]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="font-mono text-xs text-[#6e7681]">{project.title.toLowerCase()}.ts</span>
        <div className="flex items-center gap-1.5">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: statusColors[project.status] }}
          />
          <span className="font-mono text-xs" style={{ color: statusColors[project.status] }}>
            {project.status}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="p-5">
        {/* Folder icon */}
        <div className="flex items-start justify-between mb-3">
          <div className="text-[#00ff41] text-2xl">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
            </svg>
          </div>
          <div className="flex items-center gap-1 text-[#6e7681]">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
            <span className="font-mono text-xs">{project.stars}</span>
          </div>
        </div>

        <h3 className="font-mono text-lg font-bold text-[#e6edf3] mb-1 group-hover:text-[#00ff41] transition-colors">
          {project.title}
        </h3>
        <p className="font-mono text-xs text-[#00d4ff] mb-3">{project.subtitle}</p>

        {/* Description with slide-up reveal on hover */}
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait">
            {!hovered ? (
              <motion.p
                key="short"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="text-sm text-[#8b949e] leading-relaxed line-clamp-3"
              >
                {project.description}
              </motion.p>
            ) : (
              <motion.p
                key="long"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="text-sm text-[#8b949e] leading-relaxed"
              >
                {project.longDesc}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Tech tags */}
        <div className="flex flex-wrap gap-1.5 mt-4">
          {project.tech.slice(0, 5).map((t) => (
            <span key={t} className="tag text-[0.65rem]">
              {t}
            </span>
          ))}
          {project.tech.length > 5 && (
            <span className="font-mono text-[0.65rem] text-[#6e7681] px-2 py-0.5">
              +{project.tech.length - 5}
            </span>
          )}
        </div>

        {/* Links */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#30363d]">
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 font-mono text-xs text-[#6e7681] hover:text-[#00ff41] transition-colors"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
            </svg>
            Source
          </a>
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 font-mono text-xs text-[#6e7681] hover:text-[#00d4ff] transition-colors"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15,3 21,3 21,9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Live Demo
            </a>
          )}
          {project.featured && (
            <span className="ml-auto font-mono text-xs text-[#ffa657] bg-[#ffa657]/10 border border-[#ffa657]/30 px-2 py-0.5 rounded">
              Featured
            </span>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default function Projects() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' })
  const [activeFilter, setActiveFilter] = useState('All')

  const filtered =
    activeFilter === 'All'
      ? projects
      : projects.filter((p) => p.category.includes(activeFilter))

  return (
    <section id="projects" className="py-24 relative bg-[#0d1117]">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" ref={ref}>
        {/* Header */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-10"
        >
          <p className="font-mono text-[#00ff41] text-sm mb-2">// 04. projects</p>
          <h2 className="section-title">
            Selected <span>Work</span>
          </h2>
          <div className="h-0.5 w-24 bg-gradient-to-r from-[#00ff41] to-transparent mt-3" />
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="flex flex-wrap gap-2 mb-10"
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              variants={scaleIn}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 font-mono text-sm rounded-lg border transition-all duration-300 ${
                activeFilter === cat
                  ? 'bg-[#00ff41]/10 border-[#00ff41] text-[#00ff41]'
                  : 'border-[#30363d] text-[#6e7681] hover:border-[#00ff41]/40 hover:text-[#e6edf3]'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {cat}
              {cat === 'All' && (
                <span className="ml-2 text-xs text-[#6e7681]">({projects.length})</span>
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Project Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {filtered.map((project, i) => (
              <ProjectCard key={project.id} project={project} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* GitHub CTA */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="text-center mt-14"
        >
          <p className="font-mono text-sm text-[#6e7681] mb-4">
            // more projects on GitHub
          </p>
          <motion.a
            href="https://github.com/its-seraj"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
            </svg>
            View GitHub Profile
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}
